import type { User, UserRole } from '@/types';

/**
 * Lấy tất cả role names từ user (lowercase) để check quyền
 */
function getRoles(userOrRole?: User | UserRole | null): string[] {
  if (!userOrRole) return [];
  if (typeof userOrRole === 'string') return [userOrRole.toLowerCase()];
  // Nếu là User object, dùng mảng roles (chính xác hơn user.role)
  const fromArray = userOrRole.roles?.map((r) => r.roleName.toLowerCase()) ?? [];
  const fromPrimary = userOrRole.role ? [userOrRole.role] : [];
  return [...new Set([...fromArray, ...fromPrimary])];
}

export function hasRole(user: User | UserRole | null | undefined, ...roles: string[]): boolean {
  const userRoles = getRoles(user);
  return roles.some((r) => userRoles.includes(r.toLowerCase()));
}

/**
 * Check if user is approved.
 * Creating content (transfer, job, rental, etc.) requires 'approved' status.
 */
function isApproved(user?: User | null): boolean {
  return user?.approvalStatus === 'approved';
}

export const can = {
  // --- Nhóm Sang Nhượng (Business Transfer) ---
  createTransfer: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'restaurantseller'),
  deleteTransfer: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'restaurantseller'),
  sendOffer: (user?: User | null) => hasRole(user, 'admin', 'restaurantbuyer'),
  approveOffer: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'restaurantseller'),

  // --- Nhóm Cho Thuê (Rental & Property) ---
  createRental: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'landlord'),
  createRentalDemand: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'propertyseeker'),

  // --- Nhóm Tuyển Dụng (Recruitment) ---
  createJob: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'headhunter', 'headhunterclient'),
  applyJob: (user?: User | null) => hasRole(user, 'admin', 'jobseeker'),
  createCV: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'jobseeker'),
  viewCV: (user?: User | null, targetUserId?: string) => {
    if (hasRole(user, 'admin', 'headhunter', 'headhunterclient')) return true;
    if (user?.id === targetUserId) return true;
    return false;
  },

  // --- Nhóm Nội Thất & Nhượng Quyền ---
  createFurniture: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'furnitureseller'),
  buyFurniture: (user?: User | null) => hasRole(user, 'admin', 'furniturebuyer'),
  createFranchise: (user?: User | null) => isApproved(user) && hasRole(user, 'admin', 'franchiser'),
  seekFranchise: (user?: User | null) => hasRole(user, 'admin', 'franchiseseeker'),

  // --- Nhóm Hệ Thống ---
  manageUsers: (user?: User | null) => hasRole(user, 'admin'),
  manageSystem: (user?: User | null) => hasRole(user, 'admin'),
  isAdmin: (user?: User | null) => hasRole(user, 'admin'),
};
